import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Button,
  Checkbox,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  ItemTypes,
  saveUnsavedChanges,
  selectAllContent,
  selectAllGroups,
  selectCatalogGroups,
  selectGroup,
  selectSubControls,
  updateContacts,
  updateGroups,
} from '../../redux/reducers/assessmentReducer';
import { selectTitle } from '../../redux/reducers/catalogsReducer';
import PersonSelector from '../PersonSelector';
import AddContactModal from '../organization/AddContactModal';

function getDepth(id) {
  let depth = 0;
  const catalogParts = id.split(':');
  if (catalogParts.length > 2) {
    const groupParts = catalogParts.at(-1).split('.');
    depth = groupParts.length;
  }
  return depth;
}

function ScopingGroupRow({
  group,
  handleGroupSelect,
  handleGroupExpanded,
  groupExpanded,
  handlePersonSelect,
  handlePersonAdd,
}) {
  const { t } = useTranslation();
  const controls = useSelector((state) => selectSubControls(state, group.id));
  const title = useSelector((state) => selectTitle(state, group.id));
  const depth = getDepth(group.id);

  return (
    <div key={`${group.id}-div`}>
      <Stack
        direction="row"
        variant="scopingRow"
        sx={{ ml: depth * 5, mr: depth * 3, mb: depth === 0 ? 1 : 0, mt: depth > 0 ? '10px' : 0 }}
      >
        <Stack direction="row">
          <Checkbox id={`${title}-checkbox`} variant="checkbox" checked={group.inScope ?? true} onChange={() => handleGroupSelect(group.id)} />
          <Button variant="evaluationListText" onClick={() => handleGroupExpanded(group.id)}>
            <Typography id={title} variant="listH2">{title}</Typography>
          </Button>
          <IconButton id={`expand-${title}`} onClick={() => handleGroupExpanded(group.id)}>
            {groupExpanded[group.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>
        {depth > 0 && (
          <PersonSelector
            id={group.id}
            title={t('assessment.scoping.contact')}
            contacts={group.contacts}
            handlePersonChange={handlePersonSelect}
            handleAddNew={handlePersonAdd}
          />
        )}
      </Stack>
      <Collapse in={groupExpanded[group.id]}>
        {controls.map((control) => (
          control.type === ItemTypes.GROUP ? (
            <ScopingGroupRow
              key={`${control.id}-row`}
              group={control}
              handleGroupSelect={handleGroupSelect}
              handleGroupExpanded={handleGroupExpanded}
              groupExpanded={groupExpanded}
              handlePersonSelect={handlePersonSelect}
              handlePersonAdd={handlePersonAdd}
            />
          ) : (
            <ScopingControlRow
              key={`${control.id}-row`}
              control={control}
              handleGroupSelect={handleGroupSelect}
              handleGroupExpanded={handleGroupExpanded}
              groupExpanded={groupExpanded}
              handlePersonSelect={handlePersonSelect}
              handlePersonAdd={handlePersonAdd}
            />
          )
        ))}
      </Collapse>
    </div>
  );
}

function ScopingControlRow({
  control,
  handleGroupSelect,
  handleGroupExpanded,
  handlePersonSelect,
  handlePersonAdd,
}) {
  const { t } = useTranslation();
  const title = useSelector((state) => selectTitle(state, control.id));
  const depth = getDepth(control.id);

  return (
    <Stack direction="row" variant="scopingRow" sx={{ ml: depth * 5, mr: depth * 3, mt: '10px' }}>
      <Stack direction="row">
        <Checkbox id={`${title}-checkbox`} variant="checkbox" checked={control.inScope ?? true} onChange={() => handleGroupSelect(control.id)} />
        <Button id={`expand-${title}`} variant="evaluationListText" onClick={() => handleGroupExpanded(control.id)}>
          <Typography id={title} variant="listH3">{title}</Typography>
        </Button>
      </Stack>
      <PersonSelector
        id={control.id}
        title={t('assessment.scoping.contact')}
        contacts={control.contacts}
        handlePersonChange={handlePersonSelect}
        handleAddNew={handlePersonAdd}
        fill={false}
      />
    </Stack>
  );
}

function AssessmentScoping() {
  const dispatch = useDispatch();
  const catalogGroups = useSelector(selectCatalogGroups);
  const allGroups = useSelector(selectAllGroups);
  const content = useSelector(selectAllContent);
  const [showModal, setShowModal] = useState(false);
  const [callbackId, setCallbackId] = useState('');

  const initGroupExpanded = useCallback(() => {
    const initState = {};
    allGroups.forEach((group) => {
      initState[group.id] = false;
    });
    return initState;
  }, [allGroups]);

  useEffect(() => () => {
    dispatch(saveUnsavedChanges(''));
  }, []);

  const initialExpandedState = useMemo(() => initGroupExpanded(), [allGroups]);

  const [groupExpanded, setGroupExpanded] = useState(initialExpandedState);

  const handleSelect = (id) => {
    const newValue = !content.find((item) => item.id === id).inScope;
    // remove false positives, e.g. deselecting 5.1 should deselect 5.1.1 but not 5.10, but selecting the whole
    // management ISO27001 2022 should also select the base groups (i.e. ISO27001:2022:4)
    const controlsToChange = content
      .filter((item) => item.id.startsWith(id) && (item.id.includes(':', id.length) || item.id.includes('.', id.length) || item.id === id));
    // If adding a control or a group into scope, ensure the levels above are added as well
    if (newValue) {
      const splitId = id.split(':');
      if (splitId.length === 3) {
        const prefix = splitId.slice(0, -1).join(':');
        controlsToChange.push(content.find((item) => item.id === prefix));
        const splitGroups = splitId.slice(-1)[0].split('.').slice(0, -1);
        splitGroups.forEach((_, index) => {
          controlsToChange.push(content.find((item) => item.id === (`${prefix}:${splitGroups.slice(0, index + 1).join('.')}`)));
        });
      }
    }
    dispatch(updateGroups(controlsToChange.map((item) => ({ ...item, inScope: newValue }))));
  };

  const handleGroupExpanded = (groupId) => {
    const newState = { ...groupExpanded };
    if (groupExpanded[groupId]) {
      const groupsToChange = Object.keys(groupExpanded).filter((key) => key.startsWith(groupId));
      groupsToChange.forEach((group) => {
        newState[group] = false;
      });
    } else {
      newState[groupId] = true;
    }
    setGroupExpanded(newState);
  };

  const handlePersonSelect = (value, id) => {
    const controlsToChange = content.filter(
      (item) => item.id === id || (
        item.id.startsWith(id)
        && (item.id.includes(':', id.length) || item.id.includes('.', id.length))
        && (item.contacts === undefined || item.contacts.length === 0)),
    );
    dispatch(updateContacts(controlsToChange.map((item) => ({ ...item, contacts: value }))));
  };

  // TODO: check the callback id functionality if it's obsolete or should still be working
  const handlePersonAdd = (id) => {
    setCallbackId(id);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  return (
    <>
      {showModal && (
        <AddContactModal handleClose={handleModalClose} />
      )}
      <Stack variant="elevated" spacing={2}>
        {catalogGroups.map((catalog) => (
          <Paper key={catalog.id} variant="elevation" elevation={4} sx={{ p: 2 }}>
            <ScopingGroupRow
              key={`${catalog.id}-row`}
              group={catalog}
              handleGroupSelect={handleSelect}
              handleGroupExpanded={handleGroupExpanded}
              groupExpanded={groupExpanded}
              handlePersonSelect={handlePersonSelect}
              handlePersonAdd={handlePersonAdd}
            />
          </Paper>
        ))}
      </Stack>
    </>
  );
}
export default AssessmentScoping;
